import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import React, { useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { LTButton } from '@lambdatestincprivate/lt-components';
import { ChevronDownIcon, ChevronUpIcon, ClockIcon } from '@primer/octicons-react';
import { actionStepCount } from '../../../model/detail';
import { formatChipDuration } from '../../format';
import { GenerativeIcon, PauseIcon, PlayIcon } from './CommandIcons';
import { LeafCard } from './LeafCard';
import { TimelineDot } from './TimelineDot';
import { IfElseCard, UntilCard } from './ContainerCards';
import styles from './steps.module.css';

/** Connector palette (kaneai utils.ts). */
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
var LINE_GREEN = 'rgba(74, 194, 107, 0.40)';
var LINE_GREY = 'rgba(208, 215, 222, 0.70)';
var LINE_DEFAULT = '#d0d7de';

/** Rail bend SVGs — the line curves into an expanded block (quoted paths). */
var EntryBend = function EntryBend(_ref) {
  var color = _ref.color;
  return /*#__PURE__*/_jsxs("svg", {
    width: "24",
    height: "40",
    viewBox: "0 0 24 40",
    fill: "none",
    "aria-hidden": "true",
    children: [/*#__PURE__*/_jsx("line", {
      x1: "12",
      y1: "0",
      x2: "12",
      y2: "28",
      stroke: color,
      strokeWidth: "1"
    }), /*#__PURE__*/_jsx("path", {
      d: "M12 28 Q12 38 22 38",
      stroke: color,
      strokeWidth: "1",
      fill: "none"
    })]
  });
};
var ExitBend = function ExitBend(_ref2) {
  var color = _ref2.color;
  return /*#__PURE__*/_jsxs("svg", {
    width: "24",
    height: "40",
    viewBox: "0 0 24 40",
    fill: "none",
    "aria-hidden": "true",
    children: [/*#__PURE__*/_jsx("path", {
      d: "M22 2 Q12 2 12 12",
      stroke: color,
      strokeWidth: "1",
      fill: "none"
    }), /*#__PURE__*/_jsx("line", {
      x1: "12",
      y1: "12",
      x2: "12",
      y2: "40",
      stroke: color,
      strokeWidth: "1"
    })]
  });
};
var Chevron = function Chevron(_ref3) {
  var expanded = _ref3.expanded;
  return /*#__PURE__*/_jsx("svg", {
    width: "16",
    height: "16",
    viewBox: "0 0 16 16",
    fill: "none",
    "aria-hidden": "true",
    style: {
      transform: expanded ? 'rotate(90deg)' : 'rotate(0deg)',
      transition: 'transform 150ms'
    },
    children: /*#__PURE__*/_jsx("path", {
      d: "M6 4L10 8L6 12",
      stroke: "#57606A",
      strokeWidth: "1.5",
      strokeLinecap: "round",
      strokeLinejoin: "round"
    })
  });
};
/** One child row inside a block: 24px inner rail + leaf card. */
function ChildRow(_ref4) {
  var step = _ref4.step,
    childIdx = _ref4.childIdx,
    isLast = _ref4.isLast,
    ctx = _ref4.ctx,
    _ref4$inModule = _ref4.inModule,
    inModule = _ref4$inModule === void 0 ? false : _ref4$inModule;
  var i = ctx.indexOf(step.key);
  var status = ctx.displayed(step);
  return /*#__PURE__*/_jsxs("div", {
    className: styles.childRow,
    children: [/*#__PURE__*/_jsxs("div", {
      className: styles.railColumn,
      children: [childIdx === 0 ? /*#__PURE__*/_jsx("div", {
        style: {
          height: 18
        }
      }) : /*#__PURE__*/_jsx("div", {
        className: styles.railLine,
        style: {
          height: 22,
          background: ctx.topColor(i)
        }
      }), /*#__PURE__*/_jsx(TimelineDot, {
        status: status
      }), !isLast && /*#__PURE__*/_jsx("div", {
        className: "".concat(styles.railLine, " ").concat(styles.railFill),
        style: {
          background: ctx.bottomColor(i)
        }
      })]
    }), /*#__PURE__*/_jsx("div", {
      className: inModule ? styles.moduleRowContent : styles.childContent,
      children: step.ifElse ? /*#__PURE__*/_jsx(IfElseCard, {
        step: step,
        ctx: ctx
      }) : step.until ? /*#__PURE__*/_jsx(UntilCard, {
        step: step,
        ctx: ctx
      }) : /*#__PURE__*/_jsx(LeafCard, {
        step: step,
        status: status,
        selected: ctx.selectedKey === step.key,
        chipHidden: ctx.chipHidden(status),
        onSelect: ctx.onSelect
      })
    })]
  });
}

/** Block dot: derived for display only (autoplay-aware via children). */
function blockDotStatus(block, ctx) {
  var childStatuses = block.steps.map(function (s) {
    return ctx.displayed(s);
  });
  return childStatuses.includes('executing') ? 'executing' : childStatuses.every(function (s) {
    return s === 'autoplay_pending';
  }) ? 'autoplay_pending' : block.rollup;
}

/** {{variable}} and ${parameter} tokens (kaneai
 * RenderInstructionWithVariables split). */
var OBJECTIVE_TOKEN = /(\{\{[^}]+\}\}|\$\{[^}]+\})/g;
var IS_OBJECTIVE_TOKEN = /^(\{\{[^}]+\}\}|\$\{[^}]+\})$/;

/** Objective text with variables and parameters as kaneai's info pills. */
function ObjectiveText(_ref5) {
  var text = _ref5.text;
  return /*#__PURE__*/_jsx(_Fragment, {
    children: text.split(OBJECTIVE_TOKEN).map(function (part, i) {
      return IS_OBJECTIVE_TOKEN.test(part) ?
      /*#__PURE__*/
      // eslint-disable-next-line react/no-array-index-key
      _jsx("span", {
        className: styles.objectiveToken,
        children: part
      }, i) : part;
    })
  });
}
var OBJECTIVE_MAX_LINES = 3;

/** True when the text needs more than OBJECTIVE_MAX_LINES lines at its
 * current width. scrollHeight is the full content height clamped or not, so
 * the answer holds in both states; re-measured whenever the width changes. */
function useObjectiveOverflow(ref, text) {
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    overflows = _useState2[0],
    setOverflows = _useState2[1];
  useLayoutEffect(function () {
    var el = ref.current;
    if (!el) {
      return undefined;
    }
    var measure = function measure() {
      var lineHeight = parseFloat(getComputedStyle(el).lineHeight);
      setOverflows(el.scrollHeight > lineHeight * OBJECTIVE_MAX_LINES + 1);
    };
    measure();
    if (typeof ResizeObserver === 'undefined') {
      return undefined;
    }
    var observer = new ResizeObserver(measure);
    observer.observe(el);
    return function () {
      return observer.disconnect();
    };
  }, [ref, text]);
  return overflows;
}

/** kaneai GenerativeBlockV4 layout (objective header + count badge + child
 * rows) in the evidence-only Quantum gradient — see .blockCard. Long
 * objectives clamp to OBJECTIVE_MAX_LINES with a Show more / Show less
 * toggle (evidence-only; kaneai never clamps). */
function BlockCard(_ref6) {
  var _block$objective, _block$objective2, _block$objective3;
  var block = _ref6.block,
    ctx = _ref6.ctx,
    expanded = _ref6.expanded,
    onToggle = _ref6.onToggle;
  var durationSum = block.steps.reduce(function (acc, s) {
    return s.durationMs !== null ? acc + s.durationMs : acc;
  }, 0);
  var chip = formatChipDuration(durationSum > 0 ? durationSum : null);
  var blockDot = blockDotStatus(block, ctx);
  var running = blockDot === 'executing';
  var stateClass = running ? styles.blockCardRunning : block.rollup === 'failed' ? styles.blockCardFailed : '';
  // Steps inside if/else branches and until cards render INSIDE their
  // container card, not as top-level block rows.
  var visibleSteps = block.steps.filter(function (s) {
    return !s.nested && !s.orphan;
  });
  var objectiveId = useId();
  var objectiveRef = useRef(null);
  var _useState3 = useState(false),
    _useState4 = _slicedToArray(_useState3, 2),
    objectiveOpen = _useState4[0],
    setObjectiveOpen = _useState4[1];
  var objectiveOverflows = useObjectiveOverflow(objectiveRef, block.objective);
  var objectiveTruncated = objectiveOverflows && !objectiveOpen;
  var objectiveMoreRef = useRef(null);
  var toggleObjective = function toggleObjective() {
    setObjectiveOpen(function (open) {
      return !open;
    });
    if (objectiveOpen) {
      // Collapsing a long objective pulls its button up, possibly out of
      // view; bring it back once the clamped layout has painted.
      requestAnimationFrame(function () {
        var _objectiveMoreRef$cur;
        return (_objectiveMoreRef$cur = objectiveMoreRef.current) === null || _objectiveMoreRef$cur === void 0 ? void 0 : _objectiveMoreRef$cur.scrollIntoView({
          block: 'nearest'
        });
      });
    }
  };
  return /*#__PURE__*/_jsxs("div", {
    className: "".concat(styles.blockCard, " ").concat(stateClass),
    role: "group",
    "aria-label": (_block$objective = block.objective) !== null && _block$objective !== void 0 ? _block$objective : 'Steps',
    children: [running && /*#__PURE__*/_jsx("span", {
      className: styles.quantumGlow,
      "aria-hidden": "true"
    }), /*#__PURE__*/_jsxs("div", {
      className: "".concat(styles.blockHeader, " ").concat(expanded ? styles.blockHeaderExpanded : styles.blockHeaderCollapsed),
      children: [/*#__PURE__*/_jsx("button", {
        type: "button",
        className: styles.blockToggle,
        "aria-expanded": expanded,
        "aria-labelledby": block.objective ? objectiveId : undefined,
        "aria-label": block.objective ? undefined : 'Steps',
        title: objectiveTruncated ? (_block$objective2 = block.objective) !== null && _block$objective2 !== void 0 ? _block$objective2 : undefined : undefined,
        onClick: onToggle,
        "data-testid": "evidence-block-toggle",
        children: /*#__PURE__*/_jsx(Chevron, {
          expanded: expanded
        })
      }), /*#__PURE__*/_jsx(GenerativeIcon, {
        status: blockDot === 'failed' ? 'block-error' : blockDot === 'passed' ? 'block-completed' : 'block-default'
      }), /*#__PURE__*/_jsxs("div", {
        className: styles.blockObjectiveColumn,
        children: [/*#__PURE__*/_jsx("span", {
          id: objectiveId,
          ref: objectiveRef,
          className: "".concat(styles.blockObjective, " ").concat(objectiveOpen ? '' : styles.blockObjectiveClamped),
          children: /*#__PURE__*/_jsx(ObjectiveText, {
            text: (_block$objective3 = block.objective) !== null && _block$objective3 !== void 0 ? _block$objective3 : ''
          })
        }), objectiveOverflows && /*#__PURE__*/_jsx(LTButton, {
          variant: LTButton.Variant.INVISIBLE,
          size: LTButton.Size.SMALL,
          className: styles.objectiveMore,
          trailingIcon: objectiveOpen ? /*#__PURE__*/_jsx(ChevronUpIcon, {
            size: 16,
            "aria-hidden": "true"
          }) : /*#__PURE__*/_jsx(ChevronDownIcon, {
            size: 16,
            "aria-hidden": "true"
          }),
          forwardRef: objectiveMoreRef,
          onClick: toggleObjective,
          "aria-expanded": objectiveOpen,
          "aria-controls": objectiveId,
          "data-testid": "evidence-block-objective-more",
          children: objectiveOpen ? 'Show less' : 'Show more'
        })]
      }), /*#__PURE__*/_jsx("span", {
        className: styles.blockCount,
        children: visibleSteps.length
      }), chip !== null && /*#__PURE__*/_jsxs("span", {
        className: styles.durationChip,
        children: [/*#__PURE__*/_jsx(ClockIcon, {
          size: 14,
          fill: "#636C76",
          "aria-hidden": "true"
        }), chip]
      })]
    }), expanded && /*#__PURE__*/_jsx("div", {
      className: styles.blockChildren,
      role: "list",
      children: visibleSteps.map(function (step, i) {
        return /*#__PURE__*/_jsx(ChildRow, {
          step: step,
          childIdx: i,
          isLast: i === visibleSteps.length - 1,
          ctx: ctx
        }, step.key);
      })
    })]
  });
}

/** kaneai v4/assets/ModuleBlockIcon.tsx — quoted paths on the 20-unit
 * viewBox, drawn at 16px like the module header does. The source's clipPath
 * is a no-op 14×14 window the paths already sit inside; dropped so several
 * cards on one page don't share an SVG id. */
var ModuleBlockIcon = function ModuleBlockIcon(_ref7) {
  var color = _ref7.color;
  return /*#__PURE__*/_jsxs("svg", {
    width: "16",
    height: "16",
    viewBox: "0 0 20 20",
    fill: "none",
    "aria-hidden": "true",
    children: [/*#__PURE__*/_jsx("path", {
      d: "M10.4835 4.2722C10.3315 4.20287 10.1663 4.16699 9.99929 4.16699C9.83223 4.16699 9.66712 4.20287 9.51512 4.2722L4.51595 6.5472C4.41244 6.59284 4.32443 6.6676 4.26265 6.76237C4.20086 6.85713 4.16797 6.96782 4.16797 7.08095C4.16797 7.19408 4.20086 7.30477 4.26265 7.39953C4.32443 7.4943 4.41244 7.56906 4.51595 7.6147L9.52095 9.89553C9.67295 9.96486 9.83806 10.0007 10.0051 10.0007C10.1722 10.0007 10.3373 9.96486 10.4893 9.89553L15.4943 7.62053C15.5978 7.57489 15.6858 7.50013 15.7476 7.40537C15.8094 7.3106 15.8423 7.19991 15.8423 7.08678C15.8423 6.97366 15.8094 6.86297 15.7476 6.7682C15.6858 6.67343 15.5978 6.59868 15.4943 6.55303L10.4835 4.2722Z",
      stroke: color,
      strokeWidth: "1.45833",
      strokeLinecap: "round",
      strokeLinejoin: "round"
    }), /*#__PURE__*/_jsx("path", {
      d: "M15.8327 13.2959L10.4835 15.7226C10.3315 15.7919 10.1664 15.8278 9.99935 15.8278C9.83229 15.8278 9.66718 15.7919 9.51518 15.7226L4.16602 13.2959",
      stroke: color,
      strokeWidth: "1.45833",
      strokeLinecap: "round",
      strokeLinejoin: "round"
    }), /*#__PURE__*/_jsx("path", {
      d: "M15.8327 10.3789L10.4835 12.8056C10.3315 12.8749 10.1664 12.9108 9.99935 12.9108C9.83229 12.9108 9.66718 12.8749 9.51518 12.8056L4.16602 10.3789",
      stroke: color,
      strokeWidth: "1.45833",
      strokeLinecap: "round",
      strokeLinejoin: "round"
    })]
  });
};

/** True when a group renders as its own card (generative / module) rather
 * than as bare rows. */
var isGroupBlock = function isGroupBlock(block) {
  return block.module !== undefined || block.contentType !== 'flat' && block.objective !== null;
};
var hasVisibleRows = function hasVisibleRows(block) {
  return block.steps.some(function (s) {
    return !s.nested && !s.orphan;
  });
};

/** A module's direct children in order: one row per sub-group card, one
 * row per bare step (kaneai's childCount counts exactly these). */

function moduleRows(block) {
  var _block$blocks;
  return ((_block$blocks = block.blocks) !== null && _block$blocks !== void 0 ? _block$blocks : []).flatMap(function (g) {
    return isGroupBlock(g) ? [{
      kind: 'group',
      block: g
    }] : g.steps.filter(function (s) {
      return !s.nested && !s.orphan;
    }).map(function (step) {
      return {
        kind: 'step',
        step: step
      };
    });
  });
}

/**
 * kaneai ModuleBlockV4 port (read-only subset): blue-bordered card, pale
 * blue header (chevron · module glyph · name · direct-child count pill) and
 * the module's children on the inner 24px rail — generative sub-blocks keep
 * their gradient card with entry/exit bends, bare steps are leaf rows,
 * nested modules recurse. Not ported (live-app affordances): Update pill /
 * link, kebab, abort, add-step, cursor rows, run markers. The header
 * top-aligns per the viewer's 2026-08-12 rule where kaneai centers.
 */
function ModuleCard(_ref8) {
  var _block$module$name, _block$module;
  var block = _ref8.block,
    ctx = _ref8.ctx,
    expanded = _ref8.expanded,
    onToggle = _ref8.onToggle,
    isCollapsed = _ref8.isCollapsed,
    onToggleBlock = _ref8.onToggleBlock;
  var name = (_block$module$name = (_block$module = block.module) === null || _block$module === void 0 ? void 0 : _block$module.name) !== null && _block$module$name !== void 0 ? _block$module$name : 'Module';
  var rows = moduleRows(block);
  var dot = blockDotStatus(block, ctx);
  // kaneai moduleIconColor: grey while unplayed, red once a child failed,
  // green otherwise (its WARN_BUT_CONTINUE amber needs failure conditions
  // packs don't carry).
  var iconColor = dot === 'failed' ? 'var(--lt-icon-error)' : dot === 'autoplay_pending' || dot === 'executing' ? 'var(--lt-icon-disabled)' : 'var(--lt-icon-success)';
  var open = expanded && rows.length > 0;
  return /*#__PURE__*/_jsxs("div", {
    className: "".concat(styles.moduleCard, " ").concat(block.rollup === 'failed' ? styles.moduleCardFailed : ''),
    role: "group",
    "aria-label": "Module: ".concat(name),
    children: [/*#__PURE__*/_jsxs("button", {
      type: "button",
      className: "".concat(styles.moduleHeader, " ").concat(open ? '' : styles.moduleHeaderCollapsed),
      "aria-expanded": expanded,
      onClick: onToggle,
      children: [/*#__PURE__*/_jsx(Chevron, {
        expanded: expanded
      }), /*#__PURE__*/_jsx("span", {
        className: styles.moduleIconSlot,
        children: /*#__PURE__*/_jsx(ModuleBlockIcon, {
          color: iconColor
        })
      }), /*#__PURE__*/_jsx("span", {
        className: styles.moduleName,
        title: name,
        children: name
      }), rows.length > 0 && /*#__PURE__*/_jsx("span", {
        className: styles.moduleCount,
        children: rows.length
      })]
    }), open && /*#__PURE__*/_jsx("div", {
      className: styles.moduleChildren,
      role: "list",
      children: rows.map(function (row, idx) {
        var isLast = idx === rows.length - 1;
        if (row.kind === 'step') {
          return /*#__PURE__*/_jsx(ChildRow, {
            step: row.step,
            childIdx: idx,
            isLast: isLast,
            ctx: ctx,
            inModule: true
          }, row.step.key);
        }
        var g = row.block;
        var first = g.steps[0];
        var last = g.steps[g.steps.length - 1];
        var firstIdx = first ? ctx.indexOf(first.key) : 0;
        var lastIdx = last ? ctx.indexOf(last.key) : 0;
        var gExpanded = !isCollapsed(g.key);
        // kaneai: the dot always renders; bends only wrap an EXPANDED
        // sub-block that has rows, else a straight connector.
        var bends = gExpanded && hasVisibleRows(g);
        return /*#__PURE__*/_jsxs("div", {
          className: styles.childRow,
          children: [/*#__PURE__*/_jsxs("div", {
            className: styles.railColumn,
            children: [idx === 0 ? /*#__PURE__*/_jsx("div", {
              style: {
                height: 18
              }
            }) : /*#__PURE__*/_jsx("div", {
              className: styles.railLine,
              style: {
                height: 22,
                background: ctx.topColor(firstIdx)
              }
            }), /*#__PURE__*/_jsx(TimelineDot, {
              status: blockDotStatus(g, ctx)
            }), bends ? /*#__PURE__*/_jsxs(_Fragment, {
              children: [/*#__PURE__*/_jsx(EntryBend, {
                color: ctx.bottomColor(firstIdx)
              }), /*#__PURE__*/_jsx("div", {
                className: styles.railSpacer
              }), !isLast && /*#__PURE__*/_jsx(ExitBend, {
                color: ctx.bottomColor(lastIdx)
              })]
            }) : !isLast && /*#__PURE__*/_jsx("div", {
              className: "".concat(styles.railLine, " ").concat(styles.railFill),
              style: {
                background: ctx.bottomColor(lastIdx)
              }
            })]
          }), /*#__PURE__*/_jsx("div", {
            className: styles.moduleRowContent,
            children: g.module ? /*#__PURE__*/_jsx(ModuleCard, {
              block: g,
              ctx: ctx,
              expanded: gExpanded,
              onToggle: function onToggle() {
                return onToggleBlock(g.key);
              },
              isCollapsed: isCollapsed,
              onToggleBlock: onToggleBlock
            }) : /*#__PURE__*/_jsx(BlockCard, {
              block: g,
              ctx: ctx,
              expanded: gExpanded,
              onToggle: function onToggle() {
                return onToggleBlock(g.key);
              }
            })
          })]
        }, g.key);
      })
    })]
  });
}

/**
 * Port of AuteurStepsV4 (agentView): "N Steps" + Autoplay header, then the
 * instruction tree with the 24px timeline rail. Selection follows clicks,
 * ArrowUp/Down, and autoplay (owned by the page — the status bar in the
 * screenshot panel shares its state); the screenshot panel tracks selection
 * via the store.
 */
export function StepsPanel(_ref9) {
  var tree = _ref9.tree,
    loading = _ref9.loading,
    selectedKey = _ref9.selectedKey,
    onSelect = _ref9.onSelect,
    displayed = _ref9.displayed,
    autoplayActive = _ref9.autoplayActive,
    onToggleAutoplay = _ref9.onToggleAutoplay,
    scrollRef = _ref9.scrollRef;
  // Collapsed generative blocks — lifted here so the outer rail can swap
  // bends for a straight dot+line when a block is closed (v4 TIMELINE.md:
  // "collapsed → straight line, no bends, no spacer").
  var _useState5 = useState(function () {
      return new Set();
    }),
    _useState6 = _slicedToArray(_useState5, 2),
    collapsedBlocks = _useState6[0],
    setCollapsedBlocks = _useState6[1];
  var toggleBlock = function toggleBlock(key) {
    setCollapsedBlocks(function (prev) {
      var next = new Set(prev);
      if (next.has(key)) next["delete"](key);else next.add(key);
      return next;
    });
  };
  var flatSteps = useMemo(function () {
    var _tree$flatSteps;
    return (_tree$flatSteps = tree === null || tree === void 0 ? void 0 : tree.flatSteps) !== null && _tree$flatSteps !== void 0 ? _tree$flatSteps : [];
  }, [tree]);
  var indexByKey = useMemo(function () {
    var m = new Map();
    flatSteps.forEach(function (s, i) {
      return m.set(s.key, i);
    });
    return m;
  }, [flatSteps]);
  var failIdx = useMemo(function () {
    return flatSteps.findIndex(function (s) {
      return s.status === 'failed';
    });
  }, [flatSteps]);
  var ctx = {
    indexOf: function indexOf(key) {
      var _indexByKey$get;
      return (_indexByKey$get = indexByKey.get(key)) !== null && _indexByKey$get !== void 0 ? _indexByKey$get : 0;
    },
    displayed: displayed,
    topColor: function topColor(i) {
      var prev = flatSteps[i - 1];
      if (prev && displayed(prev) === 'passed') return LINE_GREEN;
      if (failIdx >= 0 && i > failIdx) return LINE_GREY;
      return LINE_DEFAULT;
    },
    bottomColor: function bottomColor(i) {
      var step = flatSteps[i];
      if (step && displayed(step) === 'passed') return LINE_GREEN;
      if (failIdx >= 0 && i >= failIdx) return LINE_GREY;
      return LINE_DEFAULT;
    },
    selectedKey: selectedKey,
    onSelect: onSelect,
    chipHidden: function chipHidden(status) {
      return status === 'executing' || status === 'autoplay_pending';
    }
  };
  var onKeyDown = function onKeyDown(event) {
    if (flatSteps.length === 0) return;
    var key = event.key;
    if (key !== 'ArrowUp' && key !== 'ArrowDown' && key !== 'Home' && key !== 'End') {
      return;
    }
    event.preventDefault();
    var current = flatSteps.findIndex(function (s) {
      return s.key === selectedKey;
    });
    var next;
    if (key === 'Home') next = 0;else if (key === 'End') next = flatSteps.length - 1;else if (key === 'ArrowUp') next = Math.max(0, current - 1);else next = Math.min(flatSteps.length - 1, current + 1);
    var target = flatSteps[next];
    if (target && target.key !== selectedKey) {
      var _scrollRef$current;
      onSelect(target.key);
      (_scrollRef$current = scrollRef.current) === null || _scrollRef$current === void 0 || (_scrollRef$current = _scrollRef$current.querySelector("[data-step-key=\"".concat(CSS.escape(target.key), "\"]"))) === null || _scrollRef$current === void 0 || _scrollRef$current.focus();
    }
  };

  // Action steps only — wrappers/orphans aren't walkable rows (matches
  // the RCA drawer's "View all steps (N)").
  var count = actionStepCount(flatSteps);
  return /*#__PURE__*/_jsxs("div", {
    className: styles.panel,
    "aria-busy": loading,
    children: [/*#__PURE__*/_jsxs("div", {
      className: styles.headerRow,
      children: [/*#__PURE__*/_jsx("span", {
        className: styles.headerCount,
        children: loading ? 'Steps' : "".concat(count, " Step").concat(count === 1 ? '' : 's')
      }), count > 1 && /*#__PURE__*/_jsxs("button", {
        type: "button",
        className: styles.autoplayButton,
        onClick: onToggleAutoplay,
        children: [autoplayActive ? /*#__PURE__*/_jsx(PauseIcon, {}) : /*#__PURE__*/_jsx(PlayIcon, {}), autoplayActive ? 'Pause' : 'Autoplay']
      })]
    }), /*#__PURE__*/_jsxs("div", {
      className: styles.scroll,
      ref: scrollRef,
      onKeyDown: onKeyDown,
      children: [loading && /*#__PURE__*/_jsx("div", {
        "aria-hidden": "true",
        className: styles.skeletons,
        children: Array.from({
          length: 6
        }, function (_, i) {
          return /*#__PURE__*/_jsxs("div", {
            className: styles.skeletonRow,
            children: [/*#__PURE__*/_jsx("span", {
              className: styles.skeletonDot
            }), /*#__PURE__*/_jsx("span", {
              className: styles.skeletonBar
            })]
          }, i);
        })
      }), !loading && (!tree || tree.source === 'empty') && /*#__PURE__*/_jsx("p", {
        className: styles.emptyNote,
        children: "This test has no step data in the pack."
      }), !loading && tree && tree.source === 'result-only' && /*#__PURE__*/_jsx("p", {
        className: styles.treeNote,
        children: "Authoring tree unavailable \u2014 showing executed steps."
      }), !loading && tree && tree.blocks.map(function (block, bi) {
        if (!isGroupBlock(block)) {
          // Bare steps render as top-level rows (kaneai plain cards).
          var topSteps = block.steps.filter(function (st) {
            return !st.nested && !st.orphan;
          });
          return topSteps.map(function (step, si) {
            var i = ctx.indexOf(step.key);
            var status = displayed(step);
            var isFirst = bi === 0 && si === 0;
            var isLast = bi === tree.blocks.length - 1 && si === topSteps.length - 1;
            return /*#__PURE__*/_jsxs("div", {
              className: styles.topRow,
              children: [/*#__PURE__*/_jsxs("div", {
                className: styles.railColumn,
                children: [isFirst ? /*#__PURE__*/_jsx("div", {
                  style: {
                    height: 12
                  }
                }) : /*#__PURE__*/_jsx("div", {
                  className: styles.railLine,
                  style: {
                    height: 16,
                    background: ctx.topColor(i)
                  }
                }), /*#__PURE__*/_jsx(TimelineDot, {
                  status: status
                }), !isLast && /*#__PURE__*/_jsx("div", {
                  className: "".concat(styles.railLine, " ").concat(styles.railFill),
                  style: {
                    background: ctx.bottomColor(i)
                  }
                })]
              }), /*#__PURE__*/_jsx("div", {
                className: styles.topContent,
                children: step.ifElse ? /*#__PURE__*/_jsx(IfElseCard, {
                  step: step,
                  ctx: ctx
                }) : step.until ? /*#__PURE__*/_jsx(UntilCard, {
                  step: step,
                  ctx: ctx
                }) : /*#__PURE__*/_jsx(LeafCard, {
                  step: step,
                  status: status,
                  selected: selectedKey === step.key,
                  chipHidden: ctx.chipHidden(status),
                  onSelect: onSelect
                })
              })]
            }, step.key);
          });
        }
        var firstIdx = block.steps[0] ? ctx.indexOf(block.steps[0].key) : 0;
        var lastIdx = block.steps[block.steps.length - 1] ? ctx.indexOf(block.steps[block.steps.length - 1].key) : 0;
        var isLastBlock = bi === tree.blocks.length - 1;
        var blockExpanded = !collapsedBlocks.has(block.key);
        // An open card with nothing inside gets the straight rail too.
        var blockOpen = blockExpanded && hasVisibleRows(block);
        return /*#__PURE__*/_jsxs("div", {
          className: styles.topRow,
          children: [/*#__PURE__*/_jsxs("div", {
            className: styles.railColumn,
            children: [bi === 0 ? /*#__PURE__*/_jsx("div", {
              style: {
                height: 12
              }
            }) : null, blockOpen ? /*#__PURE__*/_jsxs(_Fragment, {
              children: [/*#__PURE__*/_jsx(EntryBend, {
                color: ctx.topColor(firstIdx)
              }), /*#__PURE__*/_jsx("div", {
                className: styles.railSpacer
              }), !isLastBlock && /*#__PURE__*/_jsx(ExitBend, {
                color: ctx.bottomColor(lastIdx)
              })]
            }) :
            /*#__PURE__*/
            // Collapsed: the rail passes the block like any other
            // row — dot + straight connector, no bends, no spacer.
            _jsxs(_Fragment, {
              children: [bi !== 0 && /*#__PURE__*/_jsx("div", {
                className: styles.railLine,
                style: {
                  height: 16,
                  background: ctx.topColor(firstIdx)
                }
              }), /*#__PURE__*/_jsx(TimelineDot, {
                status: blockDotStatus(block, ctx)
              }), !isLastBlock && /*#__PURE__*/_jsx("div", {
                className: "".concat(styles.railLine, " ").concat(styles.railFill),
                style: {
                  background: ctx.bottomColor(lastIdx)
                }
              })]
            })]
          }), /*#__PURE__*/_jsx("div", {
            className: styles.topContent,
            children: block.module ? /*#__PURE__*/_jsx(ModuleCard, {
              block: block,
              ctx: ctx,
              expanded: blockExpanded,
              onToggle: function onToggle() {
                return toggleBlock(block.key);
              },
              isCollapsed: function isCollapsed(key) {
                return collapsedBlocks.has(key);
              },
              onToggleBlock: toggleBlock
            }) : /*#__PURE__*/_jsx(BlockCard, {
              block: block,
              ctx: ctx,
              expanded: blockExpanded,
              onToggle: function onToggle() {
                return toggleBlock(block.key);
              }
            })
          })]
        }, block.key);
      })]
    })]
  });
}